import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useStore } from 'store/useStore'
import { TimetableForm } from 'sections/LKSection/shared/PickPointSharedModule/components/TimetableForm/TimetableForm'
import { PickPointType } from 'sections/LKSection/shared/PickPointSharedModule/types'
import { Margin } from 'components/ui'
import { ErrorText } from 'components/ui/Text/Text'
import { Form, FormItem, Input, Title } from 'components/uiKit'
import { getSharedKey } from 'utils/helpers/i18n/getKeys'

import { OrderKeys } from '../../../data'
import { OrderWorkConditionsViewModel } from '../../../stores/models/OrderWorkConditionsViewModel'
import { PickPointLocation } from '../../../types'

type OrderWorkConditionsFormProps = {
  uiStore: OrderWorkConditionsViewModel
  pickPointType: PickPointType
  floorLevel: PickPointLocation
  isCreation?: boolean
  disabled?: boolean
}

type WorkConditionsStepType = {
  area: number
  warehouseArea: number
  cellingHeight: number
  dressingRoomsCount: number
}

export const OrderWorkConditionsForm = observer(
  ({ uiStore, pickPointType, floorLevel, isCreation = false, disabled = false }: OrderWorkConditionsFormProps) => {
    const { viewOrderUiStore } = useStore()

    const { t } = useTranslation()

    const getKey = (key: string) => `${OrderKeys.workConditionsStep}.${key}`

    const { control, setValue } = useForm<WorkConditionsStepType>({
      mode: 'onChange',
      defaultValues: {
        area: +(uiStore.area || 0),
        warehouseArea: +(uiStore.warehouseArea || 0),
        dressingRoomsCount: +(uiStore.dressingRoomsCount || 0),
        cellingHeight: +(uiStore.cellingHeight || 0),
      },
    })

    useEffect(() => {
      if (!isCreation && viewOrderUiStore.initialized) {
        setValue('area', +(uiStore.area || 0))
        setValue('warehouseArea', +(uiStore.warehouseArea || 0))
        setValue('cellingHeight', +(uiStore.cellingHeight || 0))
        setValue('dressingRoomsCount', +(uiStore.dressingRoomsCount || 0))
      }
    }, [viewOrderUiStore.initialized])

    return (
      <Form>
        <Title level={5}>{t(getKey('График работы ПВЗ'))}</Title>

        <TimetableForm
          uiStore={uiStore.timetable}
          pickPointType={pickPointType}
          floorLevel={floorLevel}
          disabled={disabled}
        />

        <Margin mb={12}>
          <Title level={5}>{t(getKey('Помещение и оборудование'))}</Title>
        </Margin>

        {/* mytodo: заменить react-hook-form на форму из анта */}

        <FormItem label={t(getKey('Площадь помещения'))}>
          <Controller<WorkConditionsStepType>
            name={'area'}
            control={control}
            rules={{
              required: t(getSharedKey('required')),
              min: {
                value: 1,
                message: t(getSharedKey('min'), { count: 1 }),
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Input
                  value={field.value || undefined}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    uiStore.setArea(e.target.value)
                  }}
                  placeholder="50"
                  type="number"
                  min={0}
                  disabled={disabled}
                  data-testid="area"
                />
                {fieldState.error?.message && <ErrorText error>{fieldState.error.message}</ErrorText>}
              </>
            )}
          />
        </FormItem>

        <FormItem label={t(getKey('Площадь склада'))} tooltip={t(getKey('warehouseAreaTooltip'))}>
          <Controller<WorkConditionsStepType>
            name={'warehouseArea'}
            control={control}
            rules={{
              required: t(getSharedKey('required')),
              min: {
                value: 1,
                message: t(getSharedKey('min'), { count: 1 }),
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Input
                  value={field.value || undefined}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    uiStore.setWarehouseArea(e.target.value)
                  }}
                  placeholder="30"
                  type="number"
                  min={0}
                  disabled={disabled}
                  data-testid="warehouseArea"
                />
                {fieldState.error?.message && <ErrorText error>{fieldState.error.message}</ErrorText>}
              </>
            )}
          />
        </FormItem>

        <FormItem label={t(getKey('Высота потолков'))}>
          <Controller<WorkConditionsStepType>
            name={'cellingHeight'}
            control={control}
            rules={{
              required: t(getSharedKey('required')),
              max: {
                value: 10,
                message: t(getSharedKey('max'), { count: 10 }),
              },
              min: {
                value: 2,
                message: t(getSharedKey('min'), { count: 2 }),
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Input
                  value={field.value || undefined}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    uiStore.setCellingHeight(e.target.value)
                  }}
                  placeholder="2.5"
                  type="number"
                  disabled={disabled}
                  data-testid="cellingHeight"
                />
                {fieldState.error?.message && <ErrorText error>{fieldState.error.message}</ErrorText>}
              </>
            )}
          />
        </FormItem>

        <FormItem label={t(getKey('Количество примерочных'))} tooltip={t(getKey('dressRoomsCountTooltip'))}>
          <Controller<WorkConditionsStepType>
            name={'dressingRoomsCount'}
            control={control}
            rules={{
              required: t(getSharedKey('required')),
              min: { value: 0, message: t(getSharedKey('min'), { count: 0 }) },
            }}
            render={({ field, fieldState }) => (
              <>
                <Input
                  value={field.value || undefined}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                    uiStore.setDressRoomsCount(e.target.value)
                  }}
                  placeholder="2"
                  type="number"
                  min={0}
                  disabled={disabled}
                  data-testid="dressingRoomsCount"
                />
                {fieldState.error?.message && <ErrorText error>{fieldState.error.message}</ErrorText>}
              </>
            )}
          />
        </FormItem>
      </Form>
    )
  },
)
